// notificationDeepLink.js
//
// Single source of truth that turns a notification (its `event` + `data`) into
// the correct in-app route, so clicking any notification opens the exact page
// (and tab) it refers to. The same resolver is used by:
//   - the in-app notification lists (user + admin) on click
//   - the browser/system notification popups (NotificationContext)
//   - foreground FCM web-push messages (firebaseWebPush)
//   - background FCM web-push clicks (via the /n/go redirect route)
//
// IMPORTANT: route params in this app are base64-encoded with encodeData().
// The encoding differs per destination, so we replicate the EXACT pattern each
// target page expects (see the per-builder comments below).

import { encodeData } from './encodeAndEncode';

const TRAINEE_ROLE = 'trainee';

// ---------------------------------------------------------------------------
// Encoding helpers — must match how existing navigate() calls build each route
// ---------------------------------------------------------------------------

// Coerce a (stringified) id to the same primitive the original links encoded:
// numeric ids -> Number, everything else stays a string.
function toIdValue(id) {
  if (id === null || id === undefined || id === '') return null;
  const num = Number(id);
  return Number.isFinite(num) && String(num) === String(id).trim() ? num : id;
}

// User classroom detail: navigate(`/user/AllClassroom/classroomDetails/${encodeURIComponent(encodeData(classroomId))}`)
function userClassroomPath(classroomId, tab, data = {}) {
  const value = toIdValue(classroomId);
  if (value === null) return null;
  const base = `/user/AllClassroom/classroomDetails/${encodeURIComponent(encodeData(value))}`;
  const params = new URLSearchParams();
  if (tab) params.set('active-tab', tab);
  const assignmentId = data.assignmentId ?? data.assignment_id;
  const assessmentId = data.assessmentId ?? data.assessment_id;
  if (assignmentId != null && assignmentId !== '') {
    params.set('assignment', String(assignmentId));
  }
  if (assessmentId != null && assessmentId !== '') {
    params.set('assessment', String(assessmentId));
  }
  const liveClassId = data.liveClassId ?? data.live_class_id;
  if (liveClassId != null && liveClassId !== '') {
    params.set('liveClass', String(liveClassId));
  }
  const resourceId = data.resourceId ?? data.resource_id;
  if (resourceId != null && resourceId !== '') {
    params.set('resource', String(resourceId));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

// Admin classroom dashboard: navigate(`/admin/classrooms/classroom-dashboard/${encodeData(classroomId)}`)
function adminClassroomPath(classroomId, tab, data = {}) {
  const value = toIdValue(classroomId);
  if (value === null) return null;
  const base = `/admin/classrooms/classroom-dashboard/${encodeData(value)}`;
  const params = new URLSearchParams();
  if (tab) params.set('active-tab', tab);
  const assignmentId = data.assignmentId ?? data.assignment_id;
  const assessmentId = data.assessmentId ?? data.assessment_id;
  if (assignmentId != null && assignmentId !== '') {
    params.set('assignment', String(assignmentId));
  }
  if (assessmentId != null && assessmentId !== '') {
    params.set('assessment', String(assessmentId));
  }
  const liveClassId = data.liveClassId ?? data.live_class_id;
  if (liveClassId != null && liveClassId !== '') {
    params.set('liveClass', String(liveClassId));
  }
  const resourceId = data.resourceId ?? data.resource_id;
  if (resourceId != null && resourceId !== '') {
    params.set('resource', String(resourceId));
  }
  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}

// User course details: navigate(`/user/courses/courseDetails/${encodeURIComponent(encodeData({ id: courseId }))}`)
function userCourseDetailsPath(courseId) {
  const value = toIdValue(courseId);
  if (value === null) return null;
  return `/user/courses/courseDetails/${encodeURIComponent(encodeData({ id: value }))}`;
}

// User course watch: navigate(`/user/courses/WatchCourse/${encodeURIComponent(encodeData({ id: courseId }))}`)
function userCourseWatchPath(courseId) {
  const value = toIdValue(courseId);
  if (value === null) return null;
  return `/user/courses/WatchCourse/${encodeURIComponent(encodeData({ id: value }))}`;
}

// Admin course module/management: navigate(`/admin/courses/module/${encodeData(courseId)}`)
function adminCourseModulePath(courseId) {
  const value = toIdValue(courseId);
  if (value === null) return null;
  return `/admin/courses/module/${encodeData(value)}`;
}

// ---------------------------------------------------------------------------
// Notification field extraction
// ---------------------------------------------------------------------------

export function getNotificationData(notification) {
  if (!notification) return {};
  let contentJson = notification.content_json;
  if (typeof contentJson === 'string') {
    try {
      contentJson = JSON.parse(contentJson);
    } catch (error) {
      contentJson = null;
    }
  }
  const data = (contentJson && contentJson.data) || notification.data || {};
  return data && typeof data === 'object' ? data : {};
}

export function getNotificationEvent(notification) {
  if (!notification) return null;
  const data = getNotificationData(notification);
  return notification.event || data.eventType || null;
}

// Pull an id out of `data` (preferred) or legacy top-level notification fields.
function pickId(notification, data, keys) {
  for (const key of keys) {
    if (data[key] !== undefined && data[key] !== null && data[key] !== '') {
      return data[key];
    }
  }
  for (const key of keys) {
    if (
      notification &&
      notification[key] !== undefined &&
      notification[key] !== null &&
      notification[key] !== ''
    ) {
      return notification[key];
    }
  }
  return null;
}

// ---------------------------------------------------------------------------
// Event classification
// ---------------------------------------------------------------------------

// Classroom tab keys differ slightly between user and admin pages.
const CLASSROOM_TABS = {
  assignments: { user: 'assignments', admin: 'assignments' },
  assessment: { user: 'assessment', admin: 'assessments' },
  resources: { user: 'recorded', admin: 'recorded' },
  live: { user: 'live', admin: 'live' },
  community: { user: 'community', admin: 'community' },
};

// Course events whose recipient is definitely enrolled -> open the watch page.
const ENROLLED_COURSE_EVENTS = new Set([
  'COURSE_ENROLLED',
  'COURSE_ENROLLMENT_CONFIRMED',
  'COURSE_COMPLETED',
  'COURSE_PROGRESS_MILESTONE',
  'COURSE_UPDATED',
  'COURSE_VIDEO_AVAILABLE',
  'COURSE_DOCUMENT_AVAILABLE',
  'COURSE_MODULE_ADDED',
  'COURSE_MODULE_UPDATED',
  'COURSE_CONTENT_PUBLISHED',
  'COURSE_ASSESSMENT_AVAILABLE',
  'COURSE_SURVEY_AVAILABLE',
  'SURVEY_COMPLETED',
  'ASSESSMENT_ATTEMPT_SUBMITTED',
  'ASSESSMENT_PASSED',
  'ASSESSMENT_FAILED',
]);

const ACCOUNT_PROFILE_EVENTS = new Set([
  'USER_ACCOUNT_BLOCKED',
  'USER_ACCOUNT_UNBLOCKED',
  'ACCOUNT_BLOCKED',
  'ACCOUNT_UNBLOCKED',
]);

// These fire while the user is logged out (or already logged in). Auth routes
// are not reachable from the inbox, so the list should not show "Open link".
const AUTH_ONLY_EVENTS = new Set([
  'USER_PASSWORD_RESET_OTP',
]);

function isAuthPath(path) {
  if (!path || typeof path !== 'string') return false;
  return path === '/auth' || path.startsWith('/auth/');
}

function classroomTabForEvent(event, data) {
  if (event === 'TASK_ASSIGNED') {
    if (data.assessmentId || data.assessment_id) return 'assessment';
    return 'assignments';
  }
  if (event.includes('ASSESSMENT')) return 'assessment';
  if (event.includes('ASSIGNMENT')) return 'assignments';
  if (event.includes('RESOURCE')) return 'resources';
  if (event.includes('LIVE_CLASS')) return 'live';
  if (event.includes('COMMUNITY')) return 'community';
  return null;
}

// ---------------------------------------------------------------------------
// Main resolver
// ---------------------------------------------------------------------------

/**
 * Resolve the in-app path a notification should open.
 * @param {object} notification - notification row (must expose event + content_json/data)
 * @param {string} role - viewer role (e.g. 'trainee', 'admin', 'trainer')
 * @returns {string|null} relative in-app path, or null if it cannot be resolved
 */
export function resolveNotificationTarget(notification, role) {
  const event = getNotificationEvent(notification);
  const data = getNotificationData(notification);
  const isTrainee = role === TRAINEE_ROLE;

  if (AUTH_ONLY_EVENTS.has(event)) {
    return null;
  }

  const deepLink = data.deepLink || notification?.deepLink;
  if (isAuthPath(deepLink)) {
    return null;
  }

  const courseUrl = data.courseUrl || data.course_url;
  if (courseUrl && typeof courseUrl === 'string' && courseUrl.startsWith('/')) {
    return isAuthPath(courseUrl) ? null : courseUrl;
  }

  const dashboard = isTrainee ? '/user/dashboard' : '/admin/dashboard';
  const profile = isTrainee ? '/user/profile' : '/admin/profile';

  if (!event && (data.announcementId || data.announcement_id)) {
    return null;
  }

  if (!event) {
    return dashboard;
  }

  if (ACCOUNT_PROFILE_EVENTS.has(event)) {
    return profile;
  }

  const classroomId = pickId(notification, data, ['classroomId', 'classroom_id', 'class_id']);
  const courseId = pickId(notification, data, ['courseId', 'course_id']);

  // --- CLASSROOM (+ classroom-scoped assessment/task) ----------------------
  if (event.startsWith('CLASSROOM_') || event === 'TASK_ASSIGNED' ||
      event === 'ASSESSMENT_ATTEMPT_STARTED' || event === 'ASSESSMENT_ADAPTIVE_COMPLETED') {
    // Classroom no longer reachable -> send to the classroom list.
    if (event === 'CLASSROOM_DELETED' || event === 'CLASSROOM_APPROVAL_DENIED') {
      return isTrainee ? '/user/AllClassroom' : '/admin/classrooms';
    }

    if (classroomId) {
      const tabKey = classroomTabForEvent(event, data);
      const tab = tabKey ? CLASSROOM_TABS[tabKey][isTrainee ? 'user' : 'admin'] : null;
      return isTrainee
        ? userClassroomPath(classroomId, tab, data)
        : adminClassroomPath(classroomId, tab, data);
    }

    return isTrainee ? '/user/AllClassroom' : '/admin/classrooms';
  }

  // --- COURSE (+ course-scoped assessment/survey) --------------------------
  if (event.startsWith('COURSE_') || event === 'SURVEY_COMPLETED' ||
      event === 'ASSESSMENT_ATTEMPT_SUBMITTED' || event === 'ASSESSMENT_PASSED' ||
      event === 'ASSESSMENT_FAILED') {
    if (event === 'COURSE_CERTIFICATE_ELIGIBLE') {
      return '/user/certificates';
    }
    if (event === 'COURSE_APPROVAL_REQUESTED') {
      return '/admin/approvalRequest?status=pending';
    }
    if (event === 'COURSE_REJECTED') {
      return isTrainee ? '/user/courses' : '/admin/approvalRequest?status=rejected';
    }

    if (!courseId) {
      return isTrainee ? '/user/courses' : '/admin/courses';
    }

    if (isTrainee) {
      if (ENROLLED_COURSE_EVENTS.has(event)) return userCourseWatchPath(courseId);
      return userCourseDetailsPath(courseId);
    }

    return adminCourseModulePath(courseId);
  }

  // --- HELP / TICKET / FAQ -------------------------------------------------
  if (event.startsWith('HELP_')) {
    if (event === 'HELP_FAQ_PUBLISHED') {
      return isTrainee ? '/user/help/faq' : '/admin/settings?activeTab=faq';
    }
    if (isTrainee) {
      const ticketId = pickId(notification, data, ['ticketId', 'ticket_id']);
      return ticketId
        ? `/user/help/ticket?ticketId=${encodeURIComponent(ticketId)}`
        : '/user/help/ticket';
    }
    return '/admin/settings?activeTab=query';
  }

  // --- CERTIFICATE ----------------------------------------------------------
  if (event.startsWith('CERTIFICATE_')) {
    if (event.includes('TEMPLATE')) {
      return '/admin/certificates';
    }
    return '/user/certificates';
  }

  // --- ANNOUNCEMENT ---------------------------------------------------------
  if (event.startsWith('ANNOUNCEMENT_')) {
    return null;
  }

  // --- GAMIFICATION ---------------------------------------------------------
  if (event.startsWith('GAMIFICATION_')) {
    return '/user/dashboard';
  }

  // --- FEED -----------------------------------------------------------------
  if (event.startsWith('FEED_')) {
    const postId = pickId(notification, data, ['postId', 'post_id']);
    const feedPath = isTrainee ? '/user/feed' : '/admin/feed';
    return postId ? `${feedPath}?post=${encodeURIComponent(postId)}` : feedPath;
  }

  // --- PAYMENT / INVOICE ----------------------------------------------------
  if (event.startsWith('PAYMENT_') || event === 'INVOICE_EMAIL_SENT') {
    return '/user/settings?activeTab=payment';
  }

  // --- USER / ORG / ACCOUNT -------------------------------------------------
  if (event.startsWith('USER_') || event.startsWith('ORG_')) {
    switch (event) {
      case 'USER_PROFILE_COMPLETED':
      case 'USER_PASSWORD_CHANGED':
      case 'USER_ROLE_ASSIGNED':
      case 'ORG_USER_CREATED':
        return profile;
      case 'ORG_SETTINGS_UPDATED':
        return isTrainee ? dashboard : '/admin/settings';
      default:
        return dashboard;
    }
  }

  // --- TASK -----------------------------------------------------------------
  if (event.startsWith('TASK_')) {
    if (classroomId) {
      return isTrainee
        ? userClassroomPath(classroomId, 'assignments', data)
        : adminClassroomPath(classroomId, 'assignments', data);
    }
    return dashboard;
  }

  // --- Fallback -------------------------------------------------------------
  return isAuthPath(dashboard) ? null : dashboard;
}

/**
 * Whether the inbox should show "Open link". Auth-only events (password reset
 * OTP, etc.) have no in-app destination while the user is already logged in.
 */
export function hasNotificationOpenLink(notification, role) {
  const target = resolveNotificationTarget(notification, role);
  return Boolean(target) && !isAuthPath(target);
}

export default resolveNotificationTarget;
